
import { defineComponent } from "vue";
import Spinner from "@/components/shared/Spinner.vue";
import RiskEntry from "@/components/shared/RiskEntry.vue";
import LocationActionbar from "@/components/drawers/left/LocationActionbar.vue";
import MapMarker from "@/components/map/MapMarker.vue";
import precisionLevels from "@/assets/data/precisionLevels.json";
import { displayCoordinates } from "@/util/helpers";
import { DatasetRecord } from "@/types/DatasetRecord";
import LocationAccumulations from "@/components/accumulations/LocationAccumulations.vue";
import {
  AnyAddress,
  AnyLocation,
  IDisplayItem,
  IDisplaySection,
} from "@/types/location";
import DisplaySection from "@/components/drawers/left/DisplaySection.vue";
import RiskOverrideSelector from "@/components/drawers/left/RiskOverrideSelector.vue";
import {
  BottomTab,
  DisplayItem,
  IRiskAccuracy,
  PLevel,
  RiskScoreViewer,
} from "@/types";
import { IPrintRecord } from "@/types/print";
import { DatasetLocation } from "@/types/location/DatasetLocation";
import {
  IAccumulation,
  useAccumulations,
  useAuth,
  useGammaData,
  useGammaWebServices,
  useLayout,
  useLoading,
  useLocation,
  useMap,
  useOrgSettings,
} from "@/services";
import { mapState } from "pinia";
import EnergyRatingSection from "./energy-rating/EnergyRatingSection.vue";
import { GammaAccuracy } from "@/types/location/location-accuracy";
import PrecisionAccuracy from "@/components/shared/PrecisionAccuracy.vue";

export default defineComponent({
  name: "LeftSidebar",
  components: {
    PrecisionAccuracy,
    Spinner,
    DisplaySection,
    LocationAccumulations,
    LocationActionbar,
    RiskEntry,
    MapMarker,
    RiskOverrideSelector,
    EnergyRatingSection,
  },
  data: () => ({
    openHook: 0,
    closeHook: 0,
    printRecords: [] as (IPrintRecord & IDisplaySection)[],
  }),
  computed: {
    ...mapState(useLayout, [
      "bottomActiveTab",
      "isBottomDrawerOpen",
      "version",
    ]),
    ...mapState(useMap, ["mapPosition"]),
    ...mapState(useLocation, ["location", "coordinates"]),
    ...mapState(useOrgSettings, [
      "showRags",
      "showLeftAccumulation",
      "showRiskDetails",
      "selectedBusinessLine",
    ]),
    ...mapState(useGammaData, ["hasPrecisionAccuracy"]),
    hasEpcRating(): boolean {
      return !!this.selectedBusinessLine?.epcRating;
    },
    address(): AnyAddress | null {
      return this.activeLocation?.address || null;
    },
    hasRiskOverrides(): boolean {
      return !!useOrgSettings().selectedBusinessLine?.riskLookupOverrides;
    },
    activeLocation(): AnyLocation | null {
      return useLocation().location as AnyLocation | null;
    },
    addressLinkData(): IDisplaySection[] {
      return useGammaWebServices().addressLinkData;
    },
    displaySections(): IDisplaySection[] {
      return useLocation()
        .displaySections.map((s) => {
          return {
            ...s,
            items:
              s.type === "Table" ? this.getTableDisplaySection(s) : s.items,
            isOpen: s.type !== "Table",
          } as IDisplaySection;
        })
        .concat(this.addressLinkData);
    },
    hasAccumulations() {
      return (
        useOrgSettings().accumulations?.accumulationsLookups?.length &&
        this.showLeftAccumulation
      );
    },
    datasetRecord(): null | DatasetRecord {
      return useLocation().record;
    },
    user(): Record<string, any> {
      return useAuth().user;
    },
    riskEntries(): RiskScoreViewer[] {
      return useGammaWebServices().mainRisks || [];
    },
    isLoadingRisks(): boolean {
      return useGammaWebServices().isLoadingRisks;
    },
    accumulations(): IAccumulation[] {
      return useAccumulations().accumulations;
    },
    isLoadingAccumulations(): boolean {
      return useLoading().isLoadingData("locationAccumulations");
    },
    jbaAccuracy(): IRiskAccuracy | undefined {
      return useGammaWebServices().jbaAccuracy;
    },
    title(): string {
      return (
        this.location?.title ||
        (this.coordinates ? displayCoordinates(this.coordinates) : "[]")
      );
    },
    isVisible: {
      get(): boolean {
        return useLayout().isLeftSidebarOpen;
      },
      set(value: boolean) {
        useLayout().isLeftSidebarOpen = value;
      },
    },
    precisionLabel(): string {
      const value = this.address?.precisionLevel;
      switch (true) {
        case !!this.datasetRecord:
          return this.datasetRecord?.recordType || "";
        case !!value:
          return (
            precisionLevels.find((pl: PLevel) => pl.level === value)?.label ||
            "`--`"
          );
        default:
          return this.jbaAccuracy?.Description === "Building Outline"
            ? "Address Point"
            : "Coordinate";
      }
    },
    precisionAccuracy(): GammaAccuracy | undefined {
      return useLocation().precisionAccuracy;
    },
  },
  watch: {
    activeLocation: {
      handler(val: AnyLocation | null) {
        if (val) {
          useGammaWebServices().fetchAddressLinkData(
            useLocation().location as AnyLocation,
            useOrgSettings().locationDataLookups
          );
          useAccumulations().fetchLocationAccumulations(
            useLocation().location as DatasetLocation,
            useOrgSettings().accumulations
          );
        } else {
          this.isVisible = false;
        }
      },
      immediate: true,
    },
  },
  methods: {
    createSection(name: string): IDisplaySection {
      return {
        name,
        items: [],
        type: "",
      };
    },
    toggleDrawer() {
      if (
        this.isBottomDrawerOpen &&
        this.bottomActiveTab === BottomTab.RiskDetails
      ) {
        useLayout().isBottomDrawerOpen = false;
      } else {
        if (!this.isBottomDrawerOpen) {
          useLayout().isBottomDrawerOpen = true;
        }
        if (this.bottomActiveTab !== BottomTab.RiskDetails) {
          useLayout().setBottomActiveTab(BottomTab.RiskDetails);
        }
      }
    },
    close() {
      useLocation().updateLocation(null);
      useGammaWebServices().clearScores();
    },

    getTableDisplaySection(section: IDisplaySection): any[] {
      const lcid = useLocation().location?.record?.lcId || "";
      const orgSettingsStore = useOrgSettings();
      const displayData = orgSettingsStore.dataDisplayDict.get(lcid);

      if (!displayData) {
        return section.items;
      }

      const displayItemsDict = section.items.reduce(
        (x, y) =>
          x.set((y as DisplayItem).columnDefinition?.columnName || "", y),
        new Map<string, IDisplayItem>()
      );

      return displayData.columns
        .filter((x) => x.showInLeftPanel)
        .map((x) => {
          const item = displayItemsDict.get(x.columnName);
          return {
            name: x.displayName,
            value: item?.value,
            type: item?.type,
          } as IDisplayItem;
        });
    },
  },
});
